import React from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { CACHE_KEYS, useDeleteInvestment } from "@bn/api/apiv1";
import { GET_INVESTMENT_KEY } from "@api/cacheKeys";
import { ConfirmDeleteDialog } from "@components";

export interface Props {
  investmentId: number;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after a successful delete. The table needs nothing here — the row
   * just disappears — but the investment page has to navigate away, since the
   * route it is on no longer resolves.
   */
  onSuccess?: () => void;
}

export const DeleteInvestmentDialog = ({
  investmentId,
  isOpen,
  onClose,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending,
    isError,
    error,
    reset: mutationReset,
  } = useDeleteInvestment({
    onSuccess: () => {
      handleClose();
      enqueueSnackbar(t("investment.header.deleteModal.success"), {
        variant: "success",
        autoHideDuration: 3000,
      });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_INVESTMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_BOOKINGS],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_INVESTMENT_KEY],
      });
      onSuccess?.();
    },
    retry: false,
  });

  const handleClose = () => {
    onClose();
    mutationReset();
  };

  return (
    <ConfirmDeleteDialog
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={() => mutate({ id: investmentId })}
      isDeleteRequestLoading={isPending}
      title={t("investment.header.actions.deleteInvestmentButton")}
      text={t("investment.header.deleteModal.question")}
      confirmButtonText={t(
        "investment.protocolTable.deleteDialog.actions.delete"
      )}
      cancelButtonText={t(
        "investment.protocolTable.deleteDialog.actions.close"
      )}
      errorMessage={isError ? error?.response?.data?.message : undefined}
      testId="delete-investment-confirmation-dialog"
    />
  );
};
